import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Megaphone, Info, AlertTriangle, CheckCircle2, Sparkles, Save, Eye, EyeOff, Plus, Trash2, ArrowUp, ArrowDown, Gauge } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STYLE_OPTIONS = [
  { value: 'info',         label: 'أزرق',    Icon: Info,          cls: 'bg-brand_blue/10 border-brand_blue text-brand_blue' },
  { value: 'warning',      label: 'أصفر',    Icon: AlertTriangle, cls: 'bg-amber-50 border-amber-400 text-amber-800' },
  { value: 'success',      label: 'أخضر',    Icon: CheckCircle2,  cls: 'bg-emerald-50 border-emerald-400 text-emerald-800' },
  { value: 'announcement', label: 'بنفسجي',  Icon: Sparkles,      cls: 'bg-purple-50 border-purple-400 text-purple-800' },
];
const SPEED_OPTIONS = [
  { value: 'slow',   label: 'بطيء',   sec: 60 },
  { value: 'normal', label: 'متوسط',  sec: 40 },
  { value: 'fast',   label: 'سريع',   sec: 25 },
];

const styleCfg = (v) => STYLE_OPTIONS.find((s) => s.value === v) || STYLE_OPTIONS[0];

const newItem = () => ({ text: '', style: 'info' });

export default function SuperAnnouncement() {
  const [items, setItems] = useState([newItem()]);
  const [active, setActive] = useState(false);
  const [speed, setSpeed] = useState('normal');
  const [meta, setMeta] = useState({ updated_at: null, updated_by_name: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchOne = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/announcement`);
      const arr = Array.isArray(r.data?.items) ? r.data.items : [];
      setItems(arr.length ? arr.map((it) => ({ text: it.text || '', style: it.style || 'info' })) : [newItem()]);
      setActive(!!r.data?.active);
      setSpeed(r.data?.speed || 'normal');
      setMeta({ updated_at: r.data?.updated_at, updated_by_name: r.data?.updated_by_name });
    } catch (e) {
      toast.error('فشل تحميل الشريط الإعلاني');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOne(); }, []);

  const updateItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : [newItem()]));
  const moveItem = (idx, dir) => setItems((prev) => {
    const next = [...prev];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const addItem = () => setItems((prev) => (prev.length >= 20 ? prev : [...prev, newItem()]));

  const handleSave = async () => {
    const cleaned = items.map((it) => ({ text: it.text.trim(), style: it.style })).filter((it) => it.text);
    if (active && cleaned.length === 0) {
      toast.error('أضف إعلاناً واحداً على الأقل قبل التفعيل');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/super/announcement`, {
        items: cleaned,
        active,
        speed,
      });
      toast.success('تم حفظ الشريط الإعلاني');
      const a = res.data?.announcement || {};
      setMeta({ updated_at: a.updated_at, updated_by_name: a.updated_by_name });
      window.dispatchEvent(new CustomEvent('announcement:updated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  // Live preview mirrors the actual banner marquee
  const previewItems = items.filter((it) => it.text.trim());
  const previewSec = SPEED_OPTIONS.find((s) => s.value === speed)?.sec ?? 40;

  return (
    <div className="space-y-6" dir="rtl" data-testid="super-announcement-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
          <Megaphone className="h-6 w-6 text-purple-700" />
        </div>
        <div>
          <h1 className="text-2xl font-black">الشريط الإعلاني المتحرك</h1>
          <p className="text-sm text-foreground/60 mt-1">
            أضف عدة إعلانات وتظهر في شريط واحد يتحرك أفقياً بشكل دائري لجميع المستخدمين.
          </p>
        </div>
      </div>

      {loading ? (
        <Card className="p-12 text-center text-foreground/50">جاري التحميل...</Card>
      ) : (
        <>
          {/* Live preview */}
          <div>
            <Label className="text-xs text-foreground/60 mb-2 block">معاينة الشريط:</Label>
            <PreviewMarquee items={previewItems} sec={previewSec} active={active} />
            <p className="text-[11px] text-foreground/50 mt-1">
              {active
                ? previewItems.length
                  ? `الشريط سيظهر لجميع المستخدمين، ${previewItems.length} إعلان(ات) تتحرك دورياً.`
                  : 'اكتب نص إعلان واحد على الأقل ليظهر الشريط.'
                : 'الشريط مُعطّل حالياً — فعّله من الأسفل ليظهر للمستخدمين.'}
            </p>
          </div>

          {/* Items list */}
          <Card className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">قائمة الإعلانات ({items.length}/20)</Label>
              <Button size="sm" variant="outline" onClick={addItem} disabled={items.length >= 20} className="gap-2" data-testid="announcement-add-item">
                <Plus className="h-4 w-4" />
                إضافة إعلان
              </Button>
            </div>

            <div className="space-y-3">
              {items.map((it, idx) => {
                const cfg = styleCfg(it.style);
                return (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg border-2 space-y-2 ${cfg.cls}`}
                    data-testid={`announcement-item-${idx}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-black opacity-70 flex-shrink-0">#{idx + 1}</span>
                      <Input
                        value={it.text}
                        onChange={(e) => updateItem(idx, { text: e.target.value })}
                        placeholder="اكتب نص الإعلان..."
                        maxLength={240}
                        className="text-right bg-white/80 flex-1"
                        data-testid={`announcement-item-${idx}-text`}
                      />
                      <div className="flex flex-col gap-0.5">
                        <button
                          type="button"
                          onClick={() => moveItem(idx, -1)}
                          disabled={idx === 0}
                          className="p-1 rounded hover:bg-white/60 disabled:opacity-30"
                          aria-label="نقل للأعلى"
                          data-testid={`announcement-item-${idx}-up`}
                        ><ArrowUp className="h-3.5 w-3.5" /></button>
                        <button
                          type="button"
                          onClick={() => moveItem(idx, +1)}
                          disabled={idx === items.length - 1}
                          className="p-1 rounded hover:bg-white/60 disabled:opacity-30"
                          aria-label="نقل للأسفل"
                          data-testid={`announcement-item-${idx}-down`}
                        ><ArrowDown className="h-3.5 w-3.5" /></button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="p-1.5 rounded hover:bg-white/60 text-red-700"
                        aria-label="حذف"
                        data-testid={`announcement-item-${idx}-delete`}
                      ><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pr-6">
                      {STYLE_OPTIONS.map(({ value, label, Icon }) => {
                        const activeSt = it.style === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => updateItem(idx, { style: value })}
                            className={`px-2.5 py-1 rounded-md border text-[11px] font-bold flex items-center gap-1 transition-colors ${
                              activeSt ? 'bg-white border-current' : 'bg-white/50 border-transparent opacity-60 hover:opacity-100'
                            }`}
                            data-testid={`announcement-item-${idx}-style-${value}`}
                          >
                            <Icon className="h-3 w-3" />
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Settings: speed + activation */}
          <Card className="p-5 space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Gauge className="h-4 w-4" /> سرعة الحركة</Label>
              <div className="flex flex-wrap gap-2">
                {SPEED_OPTIONS.map(({ value, label }) => {
                  const activeSp = speed === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSpeed(value)}
                      className={`px-4 py-2 rounded-md border text-sm font-bold transition-colors ${
                        activeSp ? 'border-brand_blue bg-brand_blue/10 text-brand_blue' : 'border-input bg-background text-foreground hover:bg-accent'
                      }`}
                      data-testid={`announcement-speed-${value}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>الحالة</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setActive(true)}
                  className={`px-4 py-2 rounded-md border text-sm font-bold flex items-center gap-2 transition-colors ${
                    active ? 'border-emerald-600 bg-emerald-50 text-emerald-800' : 'border-input bg-background text-foreground hover:bg-accent'
                  }`}
                  data-testid="announcement-activate"
                >
                  <Eye className="h-4 w-4" />
                  مفعّل
                </button>
                <button
                  type="button"
                  onClick={() => setActive(false)}
                  className={`px-4 py-2 rounded-md border text-sm font-bold flex items-center gap-2 transition-colors ${
                    !active ? 'border-destructive bg-destructive/10 text-destructive' : 'border-input bg-background text-foreground hover:bg-accent'
                  }`}
                  data-testid="announcement-deactivate"
                >
                  <EyeOff className="h-4 w-4" />
                  مُعطّل
                </button>
              </div>
            </div>

            {meta.updated_at && (
              <p className="text-[11px] text-foreground/50 pt-2 border-t border-border">
                آخر تحديث: <span className="font-numbers" dir="ltr">{new Date(meta.updated_at).toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                {meta.updated_by_name && <> — بواسطة {meta.updated_by_name}</>}
              </p>
            )}

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-announcement-btn">
                <Save className="h-4 w-4" />
                {saving ? 'جاري الحفظ...' : 'حفظ الشريط'}
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// Preview marquee uses the same underlying CSS animation as the global banner
function PreviewMarquee({ items, sec, active }) {
  if (!items.length) {
    return (
      <div className="px-4 py-3 rounded-lg border-2 border-dashed border-input bg-muted/40 text-foreground/50 text-sm text-center">
        اكتب رسالتك في الأسفل لعرض المعاينة
      </div>
    );
  }
  const inner = (
    <div className="announcement-marquee-track" style={{ animationDuration: `${sec}s`, animationPlayState: active ? 'running' : 'paused' }}>
      {[0, 1].map((copy) => (
        <div key={copy} className="flex items-center gap-2 pl-2" aria-hidden={copy === 1}>
          {items.map((it, i) => {
            const cfg = styleCfg(it.style);
            const Icon = cfg.Icon;
            return (
              <span key={`${copy}-${i}`} dir="rtl" style={{ unicodeBidi: 'isolate' }} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ${cfg.cls} text-sm font-bold whitespace-nowrap`}>
                <Icon className="h-3.5 w-3.5" />
                <bdi>{it.text}</bdi>
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
  return (
    <div className="announcement-marquee-viewport rounded-lg border-2 border-input bg-white overflow-hidden py-2" dir="ltr">
      {inner}
    </div>
  );
}
